<script setup lang="ts">
import GoogleMap from '@molecules/components/GoogleMap.vue';
import Marker from '@organisms/offer/selection-items/components/map/Marker.vue';
import HeaderMapButtons from '@organisms/offer/selection-items/components/map/HeaderMapButtons.vue';
import ZoomMapButtons from '@molecules/components/map/ZoomMapButtons.vue';
import { useSelectionItemMapStore } from '@organisms/offer/selection-items/stores/useSelectionItemMapStore';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import { computed } from 'vue';
import { UserAuth } from '@tools/user/auth';
import { useCustomMarkerStore } from '@organisms/offer/selection-items/stores/useCustomMarkerStore';
import { CustomMarker } from 'vue3-google-map';
import { useSelectionItemStore } from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import Icon from '@components/Icon.vue';
import IconGoogleMapsMarker from '@icons/ph_googlemaps_marker.svg';

const emit = defineEmits<{(e: 'marker-click', id: number): void}>();

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

const selectionItemStore = useSelectionItemStore();
const { setHoverSelectionItemId } = selectionItemStore;
const { hoverSelectionItemId } = storeToRefs(selectionItemStore);

const selectionItemMapStore = useSelectionItemMapStore();

const { zoomIn, zoomOut, initMap } = selectionItemMapStore;
const { markers, center } = storeToRefs(selectionItemMapStore);

const customMarkerStore = useCustomMarkerStore();
const { open: openCustomerMarkerSlider } = customMarkerStore;
const { savedDealUdurMarker } = storeToRefs(useCustomMarkerStore());

const isImpersonated = computed<boolean>(() => UserAuth.isImpersonated());
</script>

<template>
  <div
    class="search-map selection-item-map"
    :class="{ impersonated: isImpersonated }"
  >
    <HeaderMapButtons />

    <ZoomMapButtons
      v-if="!isMobileOrTablet"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
    />

    <div class="selection-item-map__container">
      <GoogleMap
        :center="center"
        height="100%"
        :is-static="false"
        :has-fullscreen-control="false"
        @ready="initMap"
      >
        <Marker
          v-for="marker in markers"
          :key="marker.id"
          :position="{
            lat: marker.lat,
            lng: marker.lng,
          }"
          :z-index="marker.id + 1"
          :is-highlighted="marker.id === hoverSelectionItemId"
          @click="() => emit('marker-click', marker.id)"
          @mouseenter="() => setHoverSelectionItemId(marker.id)"
          @mouseleave="() => setHoverSelectionItemId(null)"
        >
          {{ marker.title }}
        </Marker>

        <CustomMarker
          v-if="savedDealUdurMarker"
          :options="{
            position: {
              lat: savedDealUdurMarker.latitude,
              lng: savedDealUdurMarker.longitude,
            },
            anchorPoint: 'TOP_CENTER',
            zIndex: markers.length + 100,
          }"
          @click="openCustomerMarkerSlider"
        >
          <Icon
            :svg="IconGoogleMapsMarker"
            :width="40"
            :height="40"
          />
        </CustomMarker>
      </GoogleMap>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-map {
  height: 100%;
  right: 0;
  top: $headerSize;
  position: fixed;
  width: 100%;
  z-index: 1;

  &__container {
    display: block;
    flex-grow: 1;
    position: sticky;
    height: calc(100vh - #{$headerSize});
    z-index: 4;
  }

  @media screen and (min-width: $breakTablet) {
    min-width: rem(280);
    width: auto;
    display: block;
    flex-grow: 1;
    position: sticky;

    &__container {
      height: calc(100vh - #{$headerSize});
    }

    &.impersonated {
      top: calc(#{$headerBannerHeight} + #{$headerSize} + rem(6));

      .selection-item-map {
        &__container {
          height: calc(100vh - (#{$headerSize} + #{$headerBannerHeight} + rem(6)));
        }
      }
    }

    &.search-map {
      .zoom-map-buttons {
        top: rem(16);
      }
    }
  }
}
</style>
